import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Chip, Divider, Spinner } from "@nextui-org/react";
import useSWR from "swr";
import {
  ArchiveBoxIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import RootAdmin from "../../../components/layouts/RootAdmin";
import FilterReactSelect from "../../../components/elements/FilterReactSelect";
import { getFetcher } from "../../../services/api";
import useAuth from "../../../hooks/useAuth";
import RolesAccess from "../../../enums/roles";

const NAMA_BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

/**
 * Dasbor satu perusahaan.
 *
 * Isinya mengikuti tipe perusahaan: importir dan produsen melihat ringkasan SK
 * registrasi, pengangkut melihat rekomendasi dan kendaraannya, dan jenis laporan
 * wajibnya pun berbeda-beda. Semua penentuan itu dikerjakan di server (lihat
 * dashboardPerusahaan.service.ts) supaya aturan tipe perusahaan hanya ditulis
 * satu kali.
 *
 * Pemilih perusahaan: pengawas bebas memilih, Pengelola hanya menerima daftar
 * perusahaannya sendiri dari server - bukan disembunyikan di tampilan, tetapi
 * memang tidak dikirim.
 */
export default function DashboardPerusahaanPage() {
  const { user } = useAuth();
  const [perusahaan, setPerusahaan] = useState(null);
  const [periode, setPeriode] = useState(null);

  const { data: daftarPerusahaan, isLoading: memuatPerusahaan } = useSWR(
    "/api/dashboard/perusahaan/daftar-perusahaan",
    getFetcher,
  );
  const { data: daftarPeriode } = useSWR("/api/period/all", getFetcher);

  const opsiPerusahaan = useMemo(
    () =>
      (daftarPerusahaan?.data ?? []).map((item) => {
        // Sebagian baris Company tersimpan tanpa nama; tanpa cadangan ini
        // pilihannya tampil sebagai baris kosong yang mustahil dikenali.
        const nama = item.name?.trim() || "(Tanpa nama)";
        return {
          value: item.id,
          label: item.idCompanySimpel ? `${nama} (${item.idCompanySimpel})` : nama,
        };
      }),
    [daftarPerusahaan],
  );

  const opsiPeriode = useMemo(
    () =>
      (Array.isArray(daftarPeriode) ? daftarPeriode : []).map((item) => ({
        value: item.id,
        label: item.name,
      })),
    [daftarPeriode],
  );

  // Pengelola hanya punya satu-dua perusahaan; langsung dipilihkan supaya
  // dasbornya terisi tanpa satu klik tambahan.
  useEffect(() => {
    if (!perusahaan && opsiPerusahaan.length > 0 && daftarPerusahaan?.bolehSemua === false) {
      setPerusahaan(opsiPerusahaan[0]);
    }
  }, [opsiPerusahaan, perusahaan, daftarPerusahaan]);

  const alamat = useMemo(() => {
    if (!perusahaan?.value) return null;
    const parameter = new URLSearchParams({ companyId: perusahaan.value });
    if (periode?.value) parameter.set("periodId", periode.value);
    return `/api/dashboard/perusahaan/ringkasan?${parameter.toString()}`;
  }, [perusahaan, periode]);

  const { data, isLoading, error } = useSWR(alamat, getFetcher);

  const bolehSemua = daftarPerusahaan?.bolehSemua;
  const pengawas =
    bolehSemua ??
    !user?.roles?.every?.((peran) => peran === RolesAccess.PENGELOLA);

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader className="flex flex-col items-start gap-1">
          <p className="text-md">Dasbor Perusahaan</p>
          <p className="text-xs text-ink-500">
            Ringkasan kewajiban pengelolaan B3 sesuai tipe perusahaan.
          </p>
        </CardHeader>
        <Divider />
        <CardBody className="gap-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-ink-500">Perusahaan</label>
              <FilterReactSelect
                value={perusahaan}
                options={opsiPerusahaan}
                isLoading={memuatPerusahaan}
                setValue={setPerusahaan}
                placeholder={pengawas ? "Pilih perusahaan" : "Perusahaan Anda"}
              />
            </div>
            <div>
              <label className="text-xs text-ink-500">Periode Pelaporan</label>
              <FilterReactSelect
                value={periode}
                options={opsiPeriode}
                setValue={setPeriode}
                placeholder="Periode berjalan"
              />
            </div>
          </div>

          {!perusahaan && (
            <Kosong pesan="Pilih perusahaan terlebih dahulu untuk melihat dasbornya." />
          )}

          {perusahaan && isLoading && (
            <div className="flex justify-center py-10">
              <Spinner label="Menyusun ringkasan..." />
            </div>
          )}

          {perusahaan && error && (
            <Kosong
              pesan={
                error?.response?.data?.message ||
                "Ringkasan tidak dapat dimuat. Coba beberapa saat lagi."
              }
            />
          )}

          {perusahaan && !isLoading && !error && data && <Isi data={data} />}
        </CardBody>
      </Card>
    </RootAdmin>
  );
}

function Isi({ data }) {
  const { perusahaan, periode, jenisLaporanWajib, kewajibanPeriode, registrasi, pengangkutan, stok, pelaporanTerakhir, riwayatKepatuhan } = data;

  const totalKewajiban = (kewajibanPeriode?.perJenis ?? []).reduce(
    (jumlah, baris) => jumlah + baris.total,
    0,
  );
  const totalSudah = (kewajibanPeriode?.perJenis ?? []).reduce(
    (jumlah, baris) => jumlah + baris.sudah,
    0,
  );
  const totalBelum = totalKewajiban - totalSudah;

  return (
    <div className="flex flex-col gap-5">
      {/* Identitas perusahaan dan periode yang sedang ditampilkan */}
      <div className="rounded-ds-md border border-line bg-surface-2 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-ink-900">{perusahaan?.name}</h3>
            <p className="text-xs text-ink-500">
              {perusahaan?.idCompanySimpel ? `ID SIMPEL ${perusahaan.idCompanySimpel} · ` : ""}
              {perusahaan?.penanggungJawab || "Penanggung jawab belum diisi"}
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {(perusahaan?.tipePerusahaan ?? []).map((tipe) => (
                <Chip key={tipe} size="sm" variant="flat" color="primary">
                  {tipe}
                </Chip>
              ))}
            </div>
          </div>

          <div className="text-right">
            <p className="text-xs text-ink-500">Periode</p>
            <p className="text-sm font-semibold text-ink-900">
              {periode?.name ?? "Belum ada periode"}
            </p>
            {periode?.batasFinalisasi && (
              <p className="text-xs text-ink-500">
                Batas finalisasi {formatTanggal(periode.batasFinalisasi)}
                {typeof periode.sisaHari === "number" && (
                  <>
                    {" · "}
                    <span className={periode.sisaHari < 0 ? "text-danger" : periode.sisaHari <= 14 ? "text-warning" : ""}>
                      {periode.sisaHari < 0
                        ? `lewat ${Math.abs(periode.sisaHari)} hari`
                        : `${periode.sisaHari} hari lagi`}
                    </span>
                  </>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Angka-angka utama */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {registrasi && (
          <>
            <Kartu
              ikon={<DocumentCheckIcon className="size-5" />}
              label="SK Registrasi Aktif"
              nilai={registrasi.aktif}
              keterangan={
                registrasi.total > 0
                  ? `${registrasi.total} SK seluruhnya${registrasi.kedaluwarsa ? ` · ${registrasi.kedaluwarsa} kedaluwarsa` : ""}`
                  : "Belum ada SK berstatus selesai"
              }
            />
            <Kartu
              ikon={<CheckCircleIcon className="size-5" />}
              label="SK Sudah Dilaporkan"
              nilai={registrasi.total > 0 ? registrasi.dilaporkan : "-"}
              nada={registrasi.belumDilaporkan > 0 ? "perhatian" : "baik"}
              keterangan={
                registrasi.total > 0
                  ? `${registrasi.belumDilaporkan} SK belum dilaporkan pada periode ini`
                  : "Belum ada SK registrasi"
              }
            />
          </>
        )}

        <Kartu
          ikon={<ClipboardDocumentCheckIcon className="size-5" />}
          label="Kewajiban Periode Ini"
          nilai={totalKewajiban > 0 ? `${totalSudah}/${totalKewajiban}` : "-"}
          nada={totalBelum > 0 ? "perhatian" : totalKewajiban > 0 ? "baik" : "netral"}
          keterangan={
            totalKewajiban > 0
              ? `${totalBelum} bulan-laporan belum masuk`
              : "Belum ada kewajiban tercatat"
          }
        />

        <Kartu
          ikon={<CalendarDaysIcon className="size-5" />}
          label="Terakhir Melapor"
          nilai={pelaporanTerakhir ? formatTanggal(pelaporanTerakhir.tanggal) : "Belum ada"}
          keterangan={
            pelaporanTerakhir
              ? `${pelaporanTerakhir.periode ?? "Periode tidak tercatat"}${pelaporanTerakhir.jenis ? ` · ${pelaporanTerakhir.jenis}` : ""}`
              : "Belum ada laporan yang difinalisasi"
          }
        />

        {pengangkutan && (
          <>
            <Kartu
              ikon={<TruckIcon className="size-5" />}
              label="Rekomendasi Aktif"
              nilai={pengangkutan.rekomendasiAktif}
              keterangan={`${pengangkutan.kendaraan} kendaraan terdaftar`}
            />
            <Kartu
              ikon={<CheckCircleIcon className="size-5" />}
              label="Kendaraan Dilaporkan"
              nilai={
                pengangkutan.kewajibanKendaraan > 0
                  ? `${pengangkutan.kendaraanSudahDilaporkan}/${pengangkutan.kewajibanKendaraan}`
                  : "-"
              }
              nada={pengangkutan.kendaraanBelumDilaporkan > 0 ? "perhatian" : "netral"}
              keterangan={
                pengangkutan.kewajibanKendaraan > 0
                  ? `${pengangkutan.kendaraanBelumDilaporkan} kendaraan belum dilaporkan`
                  : "Belum ada kewajiban kendaraan pada periode ini"
              }
            />
          </>
        )}

        <Kartu
          ikon={<ArchiveBoxIcon className="size-5" />}
          label="Bahan B3 Terdaftar"
          nilai={stok?.jumlahBahan ?? 0}
          keterangan={`${stok?.bahanAdaStok ?? 0} bahan bersisa stok${stok?.permintaanTertunda ? ` · ${stok.permintaanTertunda} permintaan menunggu` : ""}`}
        />
      </div>

      {/* Kewajiban per jenis laporan */}
      <div>
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-ink-900">
            Kewajiban Pelaporan Periode {periode?.name ?? ""}
          </h4>
          <span className="text-xs text-ink-500">{sumberKewajiban(kewajibanPeriode?.sumber)}</span>
        </div>

        {(jenisLaporanWajib ?? []).length === 0 ? (
          <Kosong pesan="Tipe perusahaan ini belum memetakan jenis laporan wajib." />
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-g-700 text-white text-left">
                  <th className="px-3 py-2 font-medium">Jenis Laporan</th>
                  <th className="px-3 py-2 font-medium w-24">Sudah</th>
                  <th className="px-3 py-2 font-medium w-24">Belum</th>
                  <th className="px-3 py-2 font-medium">Bulan Belum Dilaporkan</th>
                </tr>
              </thead>
              <tbody>
                {(kewajibanPeriode?.perJenis ?? []).map((baris) => (
                  <tr key={baris.jenisLaporan} className="border-b border-line align-top">
                    <td className="px-3 py-2 text-ink-900">{baris.jenisLaporan}</td>
                    <td className="px-3 py-2 text-ink-700">
                      {baris.sudah}/{baris.total}
                    </td>
                    <td className="px-3 py-2">
                      {baris.belum > 0 ? (
                        <span className="text-warning font-medium">{baris.belum}</span>
                      ) : (
                        <span className="text-success">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {baris.bulanBelum?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {baris.bulanBelum.map((bulan) => (
                            <span
                              key={`${baris.jenisLaporan}-${bulan.tahun}-${bulan.bulan}`}
                              className="rounded-ds-xs border border-warning-line bg-warning-bg px-2 py-0.5 text-xs text-ink-700"
                            >
                              {NAMA_BULAN[bulan.bulan - 1]} {bulan.tahun}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-ink-500">Lengkap</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Daftar SK yang perlu ditindaklanjuti.
          Perusahaan tanpa SK sama sekali tidak diberi dua daftar kosong yang
          berbunyi "semua sudah dilaporkan" - itu menyesatkan. */}
      {registrasi && registrasi.total === 0 && (
        <Kosong pesan="Perusahaan ini belum memiliki SK registrasi yang selesai, sehingga belum ada kewajiban pelaporan per SK." />
      )}

      {registrasi && registrasi.total > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DaftarSK
            judul="SK Belum Dilaporkan Periode Ini"
            kosong="Semua SK sudah dilaporkan."
            daftar={registrasi.daftarBelumDilaporkan}
          />
          <DaftarSK
            judul="SK Akan Berakhir ≤ 90 Hari"
            kosong="Tidak ada SK yang mendekati masa berakhir."
            daftar={registrasi.daftarSegeraBerakhir}
            peringatan
          />
        </div>
      )}

      {/* Riwayat kepatuhan antar periode */}
      {(riwayatKepatuhan ?? []).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-ink-900">Kepatuhan Beberapa Periode Terakhir</h4>
          <div className="mt-2 flex flex-col gap-2">
            {riwayatKepatuhan.map((baris) => (
              <div key={baris.periodId} className="flex items-center gap-3">
                <span className="w-36 shrink-0 text-xs text-ink-700">{baris.periode}</span>
                <div className="h-2 flex-1 rounded-ds-pill bg-surface-3">
                  <div
                    className={`h-2 rounded-ds-pill ${baris.persen >= 100 ? "bg-success" : baris.persen >= 50 ? "bg-g-500" : "bg-warning"}`}
                    style={{ width: `${Math.min(100, baris.persen)}%` }}
                  />
                </div>
                <span className="w-28 shrink-0 text-right text-xs text-ink-500">
                  {baris.sudah}/{baris.total} ({baris.persen}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DaftarSK({ judul, daftar, kosong, peringatan }) {
  return (
    <div className="rounded-ds-md border border-line p-3">
      <h5 className="text-sm font-semibold text-ink-900">{judul}</h5>
      {daftar?.length ? (
        <ul className="mt-2 flex flex-col gap-2">
          {daftar.map((sk) => (
            <li key={sk.id} className="flex items-start gap-2 text-sm">
              {peringatan ? (
                <ExclamationTriangleIcon className="size-4 shrink-0 text-warning mt-0.5" />
              ) : (
                <DocumentCheckIcon className="size-4 shrink-0 text-ink-400 mt-0.5" />
              )}
              <div className="min-w-0">
                <p className="truncate text-ink-900">
                  {sk.nomor_surat_formatted || sk.nomor || "Tanpa nomor"}
                </p>
                <p className="text-xs text-ink-500">
                  {sk.type_registrasi === "produsen" ? "Registrasi Produsen B3" : "Registrasi Impor B3"}
                  {sk.berlaku_sampai ? ` · berlaku sampai ${formatTanggal(sk.berlaku_sampai)}` : " · seumur hidup"}
                </p>

                {/* Bahan B3 pada SK ini - satu SK bisa memuat beberapa bahan,
                    jadi ditampilkan sebagai daftar, bukan satu baris. */}
                {sk.BahanB3Registrasi?.length > 0 && (
                  <ul className="mt-1 flex flex-col gap-0.5">
                    {sk.BahanB3Registrasi.slice(0, 4).map((bahan, urutan) => (
                      <li key={`${sk.id}-${bahan.cas_number || urutan}`} className="text-xs text-ink-700">
                        <span className="rounded-ds-xs bg-surface-3 px-1.5 py-0.5 font-medium">
                          {bahan.cas_number || "CAS -"}
                        </span>{" "}
                        {bahan.nama_bahan || bahan.nama_dagang || "Nama bahan tidak tercatat"}
                      </li>
                    ))}
                    {sk.BahanB3Registrasi.length > 4 && (
                      <li className="text-xs text-ink-500">
                        +{sk.BahanB3Registrasi.length - 4} bahan lainnya
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-ink-500">{kosong}</p>
      )}
    </div>
  );
}

function Kartu({ ikon, label, nilai, keterangan, nada = "netral" }) {
  const warna = {
    netral: "text-g-700",
    baik: "text-success",
    perhatian: "text-warning",
  }[nada];

  return (
    <div className="rounded-ds-md border border-line bg-surface p-4">
      <div className="flex items-center gap-2 text-ink-500">
        <span className={warna}>{ikon}</span>
        <span className="text-xs">{label}</span>
      </div>
      <p className={`mt-1 text-2xl font-semibold ${warna}`}>{nilai}</p>
      {keterangan && <p className="mt-1 text-xs text-ink-500">{keterangan}</p>}
    </div>
  );
}

function Kosong({ pesan }) {
  return (
    <div className="rounded-ds-md border border-dashed border-line bg-surface-2 p-6 text-center text-sm text-ink-500">
      {pesan}
    </div>
  );
}

/**
 * Dari mana angka kewajiban itu berasal - ditulis apa adanya supaya pembaca
 * tahu bedanya daftar kewajiban resmi dengan hitungan dari laporan yang masuk.
 */
function sumberKewajiban(sumber) {
  if (sumber === "kewajiban") return "Sumber: daftar kewajiban periode";
  if (sumber === "laporan") return "Sumber: laporan final yang masuk (kewajiban periode belum dibangkitkan)";
  return "";
}

function formatTanggal(nilai) {
  if (!nilai) return "-";
  const tanggal = new Date(nilai);
  if (isNaN(tanggal)) return "-";
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(tanggal);
}
